/**
 * The local database.
 *
 * One IndexedDB database, `musicmentor`, shared by every kind of record. Version 3
 * adds documents, feature state, settings and practice history alongside the
 * lesson `progress` store that has been there since version 2.
 *
 * Two rules the upgrade follows, both from hard experience with IndexedDB:
 *
 *   * **Never drop or rewrite an existing store.** The upgrade only creates what
 *     is missing. A learner's lesson progress is not something to risk on a
 *     schema change.
 *   * **Migrate records on read, not inside the upgrade transaction.** Upgrades
 *     block every other tab and cannot be debugged once shipped; a pure function
 *     applied on read can be tested directly and fixed later.
 */

export const DB_NAME = 'musicmentor';

/**
 * v1 lesson progress without review scheduling; v2 added it; v3 added the
 * local-first stores; v4 adds the four sync stores.
 */
export const DB_VERSION = 4;

export const STORES = {
  progress: 'progress',
  documents: 'documents',
  featureState: 'featureState',
  settings: 'settings',
  practiceSessions: 'practiceSessions',

  // ------------------------------------------------------------------ sync
  // All four are keyed by the authenticated account, so signing out or
  // switching accounts on a shared iPad cannot leak one learner's sync state
  // into another's. Nothing here holds a credential — only record ids,
  // revisions and queued work.

  /** Per-account pull cursor and reconciliation state. */
  syncMeta: 'syncMeta',
  /** Durable queue. An operation is written here *before* any network attempt. */
  outbox: 'outbox',
  /** The revision the server last confirmed, tracked apart from the local one. */
  remoteRevisions: 'remoteRevisions',
  /** Recoverable copies kept when both sides changed the same record. */
  conflicts: 'conflicts',
} as const;

export type StoreName = (typeof STORES)[keyof typeof STORES];

let cached: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (cached) return cached;
  cached = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      // Present since v2. Created here only for a database that never had it.
      if (!db.objectStoreNames.contains(STORES.progress)) {
        db.createObjectStore(STORES.progress, { keyPath: 'lessonId' });
      }

      if (!db.objectStoreNames.contains(STORES.documents)) {
        const store = db.createObjectStore(STORES.documents, { keyPath: 'id' });
        // Listing recent work and, later, pulling changes since a cursor.
        store.createIndex('updatedAt', 'updatedAt');
      }
      if (!db.objectStoreNames.contains(STORES.featureState)) {
        db.createObjectStore(STORES.featureState, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORES.settings)) {
        db.createObjectStore(STORES.settings, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORES.practiceSessions)) {
        const store = db.createObjectStore(STORES.practiceSessions, { keyPath: 'id' });
        store.createIndex('startedAt', 'startedAt');
      }

      // v4. Each store is indexed by `accountId` so every read is scoped to one
      // learner by construction rather than by remembering to filter.
      if (!db.objectStoreNames.contains(STORES.syncMeta)) {
        db.createObjectStore(STORES.syncMeta, { keyPath: 'accountId' });
      }
      if (!db.objectStoreNames.contains(STORES.outbox)) {
        const store = db.createObjectStore(STORES.outbox, { keyPath: 'operationId' });
        store.createIndex('accountId', 'accountId');
        // Replay order: an edit must not overtake the create it depends on.
        store.createIndex('queuedAt', 'queuedAt');
      }
      if (!db.objectStoreNames.contains(STORES.remoteRevisions)) {
        // Key is `${accountId}|${kind}|${recordId}` — see `revisionKey`.
        const store = db.createObjectStore(STORES.remoteRevisions, { keyPath: 'key' });
        store.createIndex('accountId', 'accountId');
      }
      if (!db.objectStoreNames.contains(STORES.conflicts)) {
        const store = db.createObjectStore(STORES.conflicts, { keyPath: 'id' });
        store.createIndex('accountId', 'accountId');
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () =>
      reject(new Error('Another tab is holding an older version of the database open.'));
  });
  return cached;
}

/** Test seam. Production code never calls this. */
export function resetDbCache(): void {
  cached = null;
}

export function isIndexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    // Some privacy modes throw on access rather than returning undefined.
    return false;
  }
}

/**
 * Run work in one transaction and resolve when it *commits* — not when the
 * request succeeds. An interrupted write must leave the previous record intact,
 * and only `oncomplete` proves the write actually landed.
 */
export function transact<T>(
  db: IDBDatabase,
  stores: StoreName | StoreName[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  return new Promise((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(stores, mode);
    } catch (error) {
      reject(error);
      return;
    }
    let result: T;
    let failed = false;

    tx.oncomplete = () => {
      if (!failed) resolve(result);
    };
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));

    Promise.resolve(work(tx))
      .then((value) => {
        result = value;
      })
      .catch((error) => {
        failed = true;
        reject(error);
        try {
          tx.abort();
        } catch {
          // Already finished.
        }
      });
  });
}

/** Promisify a single IDBRequest. */
export function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
